'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription, DialogHeader } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { useToast } from '@/components/ui/use-toast';
import { Send, Phone, Mail, Building, MessageSquare, Shield } from 'lucide-react';
// Enhanced "I'm Human" Checkbox - Multi-layered bot detection
import { siloApi } from '@/lib/api/silo-api';

interface ContactUsModalProps {
  children: React.ReactNode;
  className?: string;
}

// Validation schema - All fields required
const contactSchema = z.object({
  name: z.string().min(2, 'Name is required (minimum 2 characters)'),
  email: z.string().email('Valid email address is required'),
  company: z.string().min(2, 'Company name is required'),
  phone: z.string()
    .min(1, 'Phone number is required')
    .transform(val => val.replace(/\D/g, ''))
    .refine(val => val.length >= 10 && val.length <= 15, {
      message: 'Phone number must be 10-15 digits'
    }),
  message: z.string().min(10, 'Message is required (minimum 10 characters)'),
  humanVerified: z.boolean().refine(val => val === true, 'Human verification is required')
});

type ContactFormData = z.infer<typeof contactSchema>;

export function ContactUsModal({ children, className = '' }: ContactUsModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isHumanVerified, setIsHumanVerified] = useState(false);
  const [formStartTime, setFormStartTime] = useState<number>(0);
  const { toast } = useToast();

  // Multi-layered bot detection setup
  React.useEffect(() => {
    if (isOpen) {
      setFormStartTime(Date.now());
      setIsHumanVerified(false);
      // Clear any bot detection flags
      if (typeof window !== 'undefined') {
        (window as any).humanInteractionDetected = false;
      }
    }
  }, [isOpen]);

  // Detect human-like interactions
  const handleHumanVerification = (checked: boolean) => {
    setIsHumanVerified(checked);
    
    if (checked && typeof window !== 'undefined') {
      // Record human interaction patterns
      (window as any).humanInteractionDetected = true;
      (window as any).humanVerificationTime = Date.now();
    }
  };

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: '',
      email: '',
      company: '',
      phone: '',
      message: '',
      humanVerified: false
    }
  });

  const onSubmit = async (data: ContactFormData) => {
    // Multi-layered bot detection
    const submissionTime = Date.now();
    const timeTaken = submissionTime - formStartTime;
    
    // Bot detection checks
    if (timeTaken < 3000) { // Too fast (less than 3 seconds)
      toast({
        title: 'Submission Too Fast',
        description: 'Please take a moment to review your message.',
        variant: 'destructive'
      });
      return;
    }
    
    if (!data.humanVerified) {
      toast({
        title: 'Human Verification Required',
        description: 'Please check the "I\'m not a robot" box.',
        variant: 'destructive'
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await siloApi.submitPublicContactForm({
        name: data.name,
        email: data.email, // KEEP EMAIL FIELD
        company: data.company, // 🚨 REQUIRED field
        phone: data.phone,
        request: data.message,
        // Bot detection metadata
        metadata: {
          source: 'website_contact_modal',
          inquiry_type: 'general_inquiry',
          form_completion_time: timeTaken,
          human_verified: data.humanVerified,
          interaction_detected: (window as any).humanInteractionDetected || false,
          user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
          timestamp: submissionTime
        }
      });
      
      toast({
        title: 'Message Sent Successfully!',
        description: 'Our team will contact you within 24 hours.',
      });
      
      form.reset();
      setTimeout(() => setIsOpen(false), 2000);
    } catch (error) {
      toast({
        title: 'Failed to send message',
        description: 'Please try again or email us directly at support@eskerium.com',
        variant: 'destructive'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild className={className}>
        {children}
      </DialogTrigger>
      <DialogContent 
        className="max-w-md md:max-w-2xl glass-dense border border-white/20 shadow-2xl"
        hideOverlay={true}
      >
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-white">Contact Us</DialogTitle>
          <DialogDescription className="text-white/70">
            We're here to help. Send us a message and we'll respond within 24 hours.
          </DialogDescription>
          <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
            <p className="text-sm text-blue-300 text-center">
              All fields are required for submission
            </p>
          </div>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="mt-4">
            {/* Two-column layout for iPad/Desktop, single column for mobile */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Left Column: Name, Email, Phone */}
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/90">Name</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Your name"
                          className="glass-input border-white/20 text-white placeholder:text-white/40"
                        />
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/90">
                        <Mail className="w-4 h-4 inline mr-1" />
                        Email
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder="your@email.com"
                          className="glass-input border-white/20 text-white placeholder:text-white/40"
                        />
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/90">
                        <Phone className="w-4 h-4 inline mr-1" />
                        Phone
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="tel"
                          placeholder="(555) 123-4567"
                          className="glass-input border-white/20 text-white placeholder:text-white/40"
                        />
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />
              </div>

              {/* Right Column: Company, Message, Verification, Buttons */}
              <div className="space-y-4">
                <FormField
                  control={form.control}
                  name="company"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/90">
                        <Building className="w-4 h-4 inline mr-1" />
                        Company
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="Your business/company name"
                          className="glass-input border-white/20 text-white placeholder:text-white/40"
                        />
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-white/90">
                        <MessageSquare className="w-4 h-4 inline mr-1" />
                        Message
                      </FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="How can we help you?"
                          className="glass-input border-white/20 text-white placeholder:text-white/40 min-h-[120px]"
                        />
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />

                {/* Enhanced "I'm Human" Checkbox */}
                <FormField
                  control={form.control}
                  name="humanVerified"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <div className="flex items-center space-x-3 p-4 bg-white/5 border border-white/10 rounded-lg">
                          <input
                            type="checkbox"
                            id="human-verification"
                            checked={field.value || false}
                            onChange={(e) => {
                              field.onChange(e.target.checked);
                              handleHumanVerification(e.target.checked);
                            }}
                            className="w-4 h-4 text-blue-600 bg-white/10 border-white/30 rounded focus:ring-blue-500 focus:ring-2"
                          />
                          <label 
                            htmlFor="human-verification" 
                            className="text-white/90 cursor-pointer flex items-center gap-2"
                          >
                            <Shield className="w-4 h-4 text-green-400" />
                            I'm not a robot
                          </label>
                        </div>
                      </FormControl>
                      <FormMessage className="text-red-400" />
                    </FormItem>
                  )}
                />

                {/* Submit Buttons */}
                <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsOpen(false)}
                className="flex-1 border-white/20 text-white hover:bg-white/10"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-gradient-to-r from-eskerium-gold to-eskerium-gold/90 text-eskerium-dark hover:from-eskerium-gold/90 hover:to-eskerium-gold"
              >
                {isSubmitting ? (
                  'Sending...'
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Message
                  </>
                )}
              </Button>
                </div>
              </div>
            </div>

            {/* Honeypot Field - Hidden from users, bots will fill it */}
            <input 
              type="text" 
              name="website" 
              tabIndex={-1} 
              autoComplete="off" 
              className="absolute -left-[9999px] opacity-0 pointer-events-none" 
              aria-hidden="true"
            />
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}

export default ContactUsModal;